import React, { useState } from 'react';
import { LogOut, Power } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { Button } from './Button';

interface LogoutButtonProps {
  variant?: 'icon' | 'text' | 'full';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  showConfirmation?: boolean;
}

export const LogoutButton: React.FC<LogoutButtonProps> = ({
  variant = 'icon',
  size = 'md',
  className = '',
  showConfirmation = false
}) => {
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const navigate = useNavigate();
  const signOut = useAuthStore((state) => state.signOut);

  const handleLogout = async () => {
    if (showConfirmation && !showConfirm) {
      setShowConfirm(true);
      return;
    }

    setIsLoggingOut(true);
    try {
      await signOut();
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Logout error:', error);
      // Even if there's an error, redirect to login
      navigate('/login', { replace: true });
    } finally {
      setIsLoggingOut(false);
      setShowConfirm(false);
    }
  };

  const handleCancel = () => {
    setShowConfirm(false);
  };

  if (showConfirm) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg p-6 max-w-sm w-full">
          <div className="flex items-center mb-4">
            <div className="flex items-center justify-center w-12 h-12 bg-red-100 rounded-full mr-4">
              <LogOut className="h-6 w-6 text-red-600" />
            </div>
            <div>
              <h3 className="text-lg font-medium text-gray-900">Sign Out</h3>
              <p className="text-sm text-gray-500">Are you sure you want to sign out?</p>
            </div>
          </div>
          
          <div className="flex space-x-3">
            <Button
              variant="outline"
              onClick={handleCancel}
              fullWidth
              disabled={isLoggingOut}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleLogout}
              fullWidth
              isLoading={isLoggingOut}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-500"
            >
              Sign Out
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (variant === 'icon') {
    return (
      <button
        onClick={handleLogout}
        disabled={isLoggingOut}
        className={`p-1 text-gray-700 hover:text-red-600 transition-colors disabled:opacity-50 ${className}`}
        title="Sign out"
      >
        {isLoggingOut ? (
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-600"></div>
        ) : (
          <LogOut className="h-6 w-6" />
        )}
      </button>
    );
  }

  if (variant === 'text') {
    return (
      <button
        onClick={handleLogout}
        disabled={isLoggingOut}
        className={`flex items-center px-4 py-3 text-gray-700 hover:bg-gray-100 hover:text-red-600 transition-colors w-full text-left disabled:opacity-50 ${className}`}
      >
        {isLoggingOut ? (
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-red-600 mr-3"></div>
        ) : (
          <LogOut className="h-5 w-5 mr-3" />
        )}
        <span>Sign Out</span>
      </button>
    );
  }

  // Full button variant
  return (
    <Button
      variant="outline"
      size={size}
      onClick={handleLogout}
      isLoading={isLoggingOut}
      disabled={isLoggingOut}
      className={`text-red-600 border-red-300 hover:bg-red-50 hover:border-red-400 focus:ring-red-500 ${className}`}
    >
      <Power className="h-4 w-4 mr-2" />
      Sign Out
    </Button>
  );
};